import { IconFileZip, IconList, IconSearch } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useAuthContext } from 'contexts/AuthContext';
import { useGetDealById } from 'views/Deals/hooks/useGetDealById';
import DocumentTable from 'views/DocumentLibrary/components/DocumentLibraryTable/DocumentLibraryTable';
import useAwsUploadFiles from 'hooks/useAWSUpload';
import { QUERY_KEYS } from 'services/urls';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { useQueryClient } from '@tanstack/react-query';
import useToast from 'hooks/useToast';
import { FEATURES_LIST } from 'services/constants';
import PageTitle from 'components/PageTitle/PageTitle';
import RenderIf from 'components/RenderIf/RenderIf';
import { NSButton, NSInput, NSLoadingScreen } from 'bricks';
import { PageSpecificKeys, useLocalStorage } from 'helpers/localStorage';
import { FileExtensions } from 'bricks/constants';
import { useDebounceState } from 'hooks/useDebounceState';
import DropzoneCard, { DROP_LOCATION } from './components/DropzoneCard/DropzoneCard';
import './Documents.scss';
import { DealDirectoryItem, IDealDirectory } from './types';
import UploadDocumentLibrary from './components/UploadDocumentModal/UploadDocumentModal';
import CreateFolderModal from './components/CreateFolderModal/CreateFolderModal';
import { DocumentFilters } from './components/DocumentFilters/DocumentFilters';
import SelectedFiltersBar from './components/DocumentFilters/SelectedFiltersBar';
import {
    useDealDirectoryOptions,
    useDeleteDirectory,
    useDeleteDocument,
    useGetDealDirectoryTree,
    useGetDocumentsByIds,
    useUpdateDirectory,
    useGetDealSubprojects,
} from './hooks';
import { IFilters } from './hooks/useGetDealDirectoryTree';
import AssociatedProjectSlideout from './components/AssociatedProjectSlideout/AssociatedProjectSlideout';
import { DOCUMENT_LIBRARY_COLUMN_OPTIONS, DOCUMENT_LIBRARY_COLUMNS, DealDocumentType } from './constants';
import ColumnsDropdown from './components/ColumnsDropdown/ColumnsDropdown';
import DocumentLibrarySlideout from './components/DocumentLibrarySlideout/DocumentLibrarySlideout';
import EditDirectoryModal from './components/EditDirectoryModal/EditDirectoryModal';
import RecentlyOpenedDocuments from './components/RecentlyOpenedDocuments/RecentlyOpenedDocuments';
import { FileMeta, UploadedFileMeta, useDocumentUploader } from './hooks/useDocumentUploader';
import { useFilePicker } from './hooks/useFilePicker';

const Documents = () => {
    const { selectedOrganizationId } = useAuthContext();
    const [currentSelectedFiltersLS, updateSelectedFiltersLS] = useLocalStorage(PageSpecificKeys.DocLibraryFilters, {});
    const { dealId } = useParams<{ dealId: string }>();
    const { showError, showSuccess } = useToast();
    const queryClient = useQueryClient();

    const [visibleColumns, setVisibleColumns] = useState(DOCUMENT_LIBRARY_COLUMNS.reduce((acc, column) => {
        acc[column] = true;
        return acc;
    }, {} as Record<string, boolean>));

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [showAssociatedprojectsSlideout, setShowAssociatedprojectsSlideout] = useState(false);
    const [cardView, setCardView] = useState(false);
    const [selectedDirectoryId, setSelectedDirectoryId] = useState<number>(0);
    const [searchValue, debouncedSearchValue, setSearchValue] = useDebounceState('');
    const [currentDirCardView, setCurrentDirCardView] = useState<number>(0);
    const [selectedDocumentId, setSelectedDocumentId] = useState<number>();
    const [openDocumentSlideout, setOpenDocumentSlideout] = useState(false);

    const [isEditingDocument, setIsEditingDocument] = useState<boolean>(false);

    const [itemToDelete, setItemToDelete] = useState<DealDirectoryItem | null>(null);
    const [itemToUpdate, setItemToUpdate] = useState<DealDirectoryItem | null>(null);

    const [newFilesToDisplay, setNewFilesToDisplay] = useState<string[]>([]);
    const [newFoldersToDisplay, setNewFoldersToDisplay] = useState<string[]>([]);

    const [selectedFilters, setSelectedFilters] = useState<Partial<Record<string, { value: string; label: string }[]>>>(currentSelectedFiltersLS);
    const [filtersForApi, setFiltersForApi] = useState<IFilters>({
        types: [],
        formats: [],
    });

    const [recentlyViewedDocumentsByDeal, setRecentlyViewedDocumentsByDeal] = useLocalStorage<Record<string, number[]>>(
        PageSpecificKeys.DocLibraryRecentlyViewed,
        {},
    );

    const recentlyViewedDocumentIds = dealId && dealId.trim()
        ? recentlyViewedDocumentsByDeal[dealId] || []
        : [];

    const folderLocationOptions = useDealDirectoryOptions(Number(dealId));
    const typeOptions = Object.values(DealDocumentType).map(value => ({ value, label: value }));
    const formatOptions = Object.values(FileExtensions).map(value => ({ value, label: value }));

    const {
        data: associatedProjects,
        isSuccess: isAssociatedProjectsSuccess,
    } = useGetDealSubprojects(Number(dealId));

    const { data: dealDirectoryTree, isLoading: isLoadingTree, isError: isDirectoryTreeError, error: directoryTreeError } = useGetDealDirectoryTree(
        {
            dealId: Number(dealId)!,
            options: { enabled: !!dealId },
            filters: filtersForApi,
            searchValue: debouncedSearchValue,
        },
    );
    const { data: recentlyViewedDocuments } = useGetDocumentsByIds({
        documentIds: recentlyViewedDocumentIds,
        options: { enabled: !!recentlyViewedDocumentIds.length },
    });
    const { mutate: deleteDocument } = useDeleteDocument();
    const { mutate: deleteDirectory } = useDeleteDirectory();
    const { mutate: updateDirectory } = useUpdateDirectory();

    const isTreeEmpty = useMemo(() => !isLoadingTree
        && dealDirectoryTree
        && dealDirectoryTree.documents?.length === 0
        && dealDirectoryTree.children?.length === 0,
    [dealDirectoryTree, isLoadingTree]);

    const isFilterOrSearchApplied = (
        selectedFilters && Object.values(selectedFilters).some(filter => filter && filter.length > 0)
    ) || debouncedSearchValue;

    const {
        selectedHTML5FileToUpload,
        setSelectedHTML5FileToUpload,
        isReadyToUpload,
        clearAttachmentsStates,
        handleUpload,
        awsUploadedFiles,
        setAwsUploadedFiles,
        filesToUploadRef,
        setIsReadyToUpload,
        uploadFilesWithLimit,
    } = useAwsUploadFiles({
        organizationCorrelationId: selectedOrganizationId!,
        feature: FEATURES_LIST.DEAL_DOCUMENTS,
        onError: (message: string) => {
            showError(`Failed to upload file: ${message}`);
        },
    });

    const handleDocumentSelect = (selectedDocumentId: number) => {
        setRecentlyViewedDocumentsByDeal(prev => {
            const updated = {
                ...prev,
                [dealId || '']: [selectedDocumentId, ...(prev[dealId || ''] || []).filter(id => id !== selectedDocumentId)].slice(0, 10),
            };
            return updated;
        });
        setSelectedDocumentId(selectedDocumentId);
        setOpenDocumentSlideout(true);
    };

    const handleDocumentEdit = (selectedDocumentId: number) => {
        handleDocumentSelect(selectedDocumentId);
        setIsEditingDocument(true);
    };

    const handleDeleteDocument = (docId: number) => {
        deleteDocument(docId, {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DEAL_DIRECTORY_TREE, Number(dealId)] });
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCUMENTS] });
                showSuccess('Document deleted successfully');
                setOpenDocumentSlideout(false);
                setSelectedDocumentId(undefined);
            },
            onError: (error: any) => {
                showError('Failed to delete document. Please try again.');
            },
        });
    };

    const toggleCreateFolderModal = () => setShowCreateFolderModal(prev => !prev);
    const toggleAssociatedprojectsSlideout = () => setShowAssociatedprojectsSlideout(prev => !prev);
    const toggleUploadModal = () => {
        setShowUploadModal(prev => !prev);
        clearAttachmentsStates();
        setNewFilesToDisplay([]);
        setNewFoldersToDisplay([]);
        setSelectedDirectoryId(0);
    };
    const toggleCardView = () => setCardView(prev => !prev);
    const { data: fetchedDeal, isLoading: isLoadingDeal } = useGetDealById({
        orgId: selectedOrganizationId!,
        dealId: Number(dealId),
        shouldFetch: !!Number(dealId),
    });
    const toggleDocumentSlideout = useCallback(() => {
        setOpenDocumentSlideout(prev => !prev);
        setIsEditingDocument(false);
    }, []);

    const getFilesFromEvent = useFilePicker({
        onAddFiles: newFiles => setSelectedHTML5FileToUpload(prev => [...prev, ...newFiles]),
        onCardDrop: async files => {
            setSelectedDirectoryId(currentDirCardView);
            const toUpload = files.map(f => ({ id: f.id, file: f.file }));
            filesToUploadRef.current = toUpload;
            setIsReadyToUpload(true);
            await uploadFilesWithLimit(toUpload);
        },
        onNewFolders: folders => setNewFoldersToDisplay(prev => [...new Set([...prev, ...folders])]),
        onNewTopLevelFiles: names => setNewFilesToDisplay(prev => [...new Set([...prev, ...names])]),
        currentDirCardView,
    });

    const dealIdNum = Number(dealId);
    const organizationId = selectedOrganizationId!;
    const uploadFiles = useDocumentUploader({
        dealId: dealIdNum,
        organizationId,
        selectedDirectoryId,
        showSuccess,
        showError,
        clearAttachmentsStates,
        setAwsUploadedFiles,
        setSelectedDirectoryId,
        setShowUploadModal,
        setNewFoldersToDisplay,
        setNewFilesToDisplay,
    });

    useEffect(() => {
        if (
            awsUploadedFiles.length
          && awsUploadedFiles.length === selectedHTML5FileToUpload.length
          && isReadyToUpload
        ) {
            uploadFiles(
            selectedHTML5FileToUpload as FileMeta[],
            awsUploadedFiles as UploadedFileMeta[],
            );
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [awsUploadedFiles, isReadyToUpload]);

    useEffect(() => {
        function mapOptionsToValues<T>(values?: { value: T; label: string }[]): T[] {
            return values?.map(val => val.value) ?? [];
        }

        setFiltersForApi({
            types: mapOptionsToValues<DealDocumentType>(selectedFilters.types as { value: DealDocumentType; label: string }[] | undefined),
            formats: mapOptionsToValues<FileExtensions>(selectedFilters.formats as { value: FileExtensions; label: string }[] | undefined),
        });
        updateSelectedFiltersLS(selectedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilters]);

    useEffect(() => {
        if (isDirectoryTreeError) {
            showError('Failed to load document library. Please try again.');
        }
    }, [isDirectoryTreeError, directoryTreeError, showError]);

    const handleEditDirectory = (updatedDirectory: IDealDirectory) => {
        updateDirectory({
            data: {
                name: updatedDirectory.directoryName,
                parentDirectoryId: updatedDirectory.parentDirectoryId,
            },
            directoryId: updatedDirectory.directoryId,
            dealId: Number(dealId),
        }, {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DEAL_DIRECTORY_TREE, Number(dealId)] });
                setItemToUpdate(null);
            },
        });
    };

    const handleConfirmDelete = () => {
        if (!itemToDelete) return;
        if ('extension' in itemToDelete) {
            handleDeleteDocument(itemToDelete.id);
        } else {
            deleteDirectory({ dealId: Number(dealId), directoryId: itemToDelete.directoryId }, {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DEAL_DIRECTORY_TREE, Number(dealId)] });
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCUMENTS] });
                },
            });
        }

        setItemToDelete(null);
    };

    return (
        <>
            <RenderIf isTrue={!fetchedDeal && !isLoadingDeal}>
                <div>No deal selected</div>
            </RenderIf>
            <PageTitle
                title="Documents"
                displayBreadcrumb
                dealName={fetchedDeal?.dealName}
                pageMetaDataTitle="Documents"
                containerClassName="mb-3"
                beta
                betaCustomText="Beta"
            >
                <NSButton
                    color="secondary"
                    outline={false}
                    callback={toggleAssociatedprojectsSlideout}
                    text={isAssociatedProjectsSuccess
                        ? `Associated projects (${associatedProjects.length})`
                        : 'Associated projects'}
                    className="mr-2"
                />
                <NSButton
                    className="mr-2 heap-deal-documents-create-folder"
                    color="secondary"
                    outline={false}
                    callback={toggleCreateFolderModal}
                    text="Create folder"
                />
                <NSButton
                    className="heap-deal-documents-upload-file"
                    color="primary"
                    outline={false}
                    callback={toggleUploadModal}
                    text="Upload file"
                />
            </PageTitle>
            <div>
                <RecentlyOpenedDocuments
                    recentlyViewedDocuments={recentlyViewedDocuments}
                    handleDocumentSelect={handleDocumentSelect}
                    handleDocumentEdit={handleDocumentEdit}
                    handleDeleteDocument={handleDeleteDocument}
                />
                {(!isTreeEmpty || isFilterOrSearchApplied) && (
                    <div className="d-flex flex-row justify-content-between align-items-center">
                        <div className="d-flex">
                            <NSButton
                                color="secondary"
                                outline={false}
                                icon={cardView ? IconList : IconFileZip}
                                callback={toggleCardView}
                                text={cardView ? 'List' : 'Card'}
                                className="mr-1"
                            />
                            <DocumentFilters
                                typeOptions={typeOptions}
                                formatOptions={formatOptions}
                                onFiltersChange={(
                                    updatedFilters: Partial<Record<string, { value: string; label: string }[]>>,
                                ) => setSelectedFilters(updatedFilters)}
                                selectedFilters={selectedFilters}
                            />
                            <SelectedFiltersBar selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} />
                        </div>
                        <div className="d-flex flex-row ">
                            {!cardView && (
                                <ColumnsDropdown
                                    visibleColumns={visibleColumns}
                                    updateVisibleColumns={setVisibleColumns}
                                    columnOptions={DOCUMENT_LIBRARY_COLUMN_OPTIONS}
                                />
                            )}
                            <NSInput
                                type="search"
                                name="search-bar"
                                placeholder="Search"
                                value={searchValue}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchValue(event.target.value)}
                                customClassName="bg-secondary border-0 px-0 w-100"
                                inputGroupClassName="bg-secondary border-0"
                                prependInputAddon={<NSIcon className="text-muted" icon={IconSearch} />}
                            />
                        </div>
                    </div>
                )}
                {isTreeEmpty && !isFilterOrSearchApplied && (
                    <div className="Documents__dropzone-card--container pt-2">
                        <DropzoneCard getFilesFromEvent={getFilesFromEvent} />
                    </div>
                )}
                {(isLoadingTree || isLoadingDeal) && <NSLoadingScreen />}
                {dealDirectoryTree && (!isTreeEmpty || isFilterOrSearchApplied) && (
                    <div className="mt-3">
                        <DocumentTable
                            data={dealDirectoryTree}
                            visibleColumns={visibleColumns}
                            onDocumentSelect={handleDocumentSelect}
                            onDocumentEdit={handleDocumentEdit}
                            isCardView={cardView}
                            getFilesFromEvent={getFilesFromEvent}
                            setCurrentDirCardView={setCurrentDirCardView}
                            setItemToDelete={setItemToDelete}
                            setItemToUpdate={setItemToUpdate}
                        />
                    </div>
                )}
                {isTreeEmpty && isFilterOrSearchApplied && (
                    <div className="d-flex flex-column align-items-center justify-content-center mt-5">
                        <h4>No results found</h4>
                        <span className="text-muted">Try adjusting or clearing some of your filters to see more content.</span>
                    </div>
                )}
            </div>
            <UploadDocumentLibrary
                directoryTree={dealDirectoryTree}
                toggle={toggleUploadModal}
                isOpen={showUploadModal}
                getFilesFromEvent={
                    (event: React.DragEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>) => getFilesFromEvent(event, DROP_LOCATION.MODAL)
                }
                onConfirm={handleUpload}
                filesToUpload={selectedHTML5FileToUpload}
                setFilesToUpload={setSelectedHTML5FileToUpload}
                selectedDirectoryId={selectedDirectoryId}
                setSelectedDirectoryId={setSelectedDirectoryId}
                backdrop="static"
                newFoldersToDisplay={newFoldersToDisplay}
                setNewFoldersToDisplay={setNewFoldersToDisplay}
                newFilesToDisplay={newFilesToDisplay}
                setNewFilesToDisplay={setNewFilesToDisplay}
            />
            {
                showCreateFolderModal && <CreateFolderModal isOpen toggle={toggleCreateFolderModal} />
            }
            {
                showAssociatedprojectsSlideout && associatedProjects
                && (
                    <AssociatedProjectSlideout
                        organizationId={selectedOrganizationId!}
                        dealId={Number(dealId)}
                        isOpen={showAssociatedprojectsSlideout}
                        toggle={toggleAssociatedprojectsSlideout}
                        associatedProjects={associatedProjects}
                    />
                )
            }
            {selectedDocumentId && (
                <DocumentLibrarySlideout
                    isOpen={openDocumentSlideout}
                    toggle={toggleDocumentSlideout}
                    documentId={selectedDocumentId}
                    onDelete={() => handleDeleteDocument(selectedDocumentId)}
                    setEditMode={setIsEditingDocument}
                    editMode={isEditingDocument}
                />
            )}
            <EditDirectoryModal
                isOpen={Boolean(itemToUpdate && !('extensiion' in itemToUpdate))}
                toggle={() => setItemToUpdate(null)}
                directory={itemToUpdate as IDealDirectory}
                folderOptions={folderLocationOptions}
                handleEditDirectory={handleEditDirectory}
            />
            <ConfirmationModal
                isOpen={!!itemToDelete}
                danger
                toggle={() => setItemToDelete(null)}
                onCancel={() => setItemToDelete(null)}
                onConfirm={handleConfirmDelete}
                modalHeaderText={`Delete ${itemToDelete && 'extension' in itemToDelete ? 'document' : 'folder'}`}
            >
                <p>
                    Are you sure you want to delete
                    {
                        ` ${itemToDelete && 'extension' in itemToDelete! ? 'document ' : 'folder'}`
                    }
                    <i>
                        {
                            ` ${itemToDelete && 'extension' in itemToDelete! ? itemToDelete?.name : itemToDelete?.directoryName}`
                        }
                    </i>
                    ?
                </p>
            </ConfirmationModal>
        </>
    );
};

export default Documents;
